/**
 * Period constants for target tracking
 */

export const QUARTERS = ["Q1", "Q2", "Q3", "Q4"];

export const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

export const formatMonth = (month) =>
  new Date(2000, month - 1).toLocaleString("en", { month: "short" });

export const formatReportingYearPeriod = (reportingYear) => {
  if (!reportingYear) return "";

  return `${reportingYear.startYear} (${MONTHS[reportingYear.startMonth - 1]} ${
    reportingYear.startYear
  } - ${MONTHS[reportingYear.endMonth - 1]} ${reportingYear.endYear})`;
};

export const getYearsInReportingYear = (reportingYear = {}) => {
  const startYear = Number(reportingYear.startYear);
  const endYear = Number(reportingYear.endYear ?? reportingYear.startYear);
  if (!Number.isFinite(startYear) || !Number.isFinite(endYear)) return [];

  const years = [];
  for (let year = startYear; year <= endYear; year += 1) {
    years.push(year);
  }
  return years;
};

const toMonthIndex = (year, month) => year * 12 + (month - 1);

const parseYearMonth = (entryValue) => {
  if (entryValue == null || entryValue === "") return null;

  if (entryValue instanceof Date && !Number.isNaN(entryValue.getTime())) {
    return {
      year: entryValue.getUTCFullYear(),
      month: entryValue.getUTCMonth() + 1,
    };
  }

  if (typeof entryValue === "object") {
    const year = Number(entryValue.year);
    const month = Number(entryValue.month);

    if (
      Number.isFinite(year) &&
      Number.isFinite(month) &&
      month >= 1 &&
      month <= 12
    ) {
      return { year, month };
    }
  }

  const value = String(entryValue).trim();
  const match = value.match(/^(\d{4})(?:-(\d{1,2})(?:-\d{1,2})?)?/);
  if (!match) return null;

  const year = Number(match[1]);
  const month = match[2] ? Number(match[2]) : null;

  if (!Number.isFinite(year)) return null;
  if (month != null && (!Number.isFinite(month) || month < 1 || month > 12)) {
    return null;
  }

  return { year, month };
};

const isMonthWithinReportingYear = (reportingYear = {}, entryValue = {}) => {
  const startYear = Number(reportingYear.startYear);
  const startMonth = Number(reportingYear.startMonth);
  const endYear = Number(reportingYear.endYear);
  const endMonth = Number(reportingYear.endMonth);

  if (
    !Number.isFinite(startYear) ||
    !Number.isFinite(startMonth) ||
    !Number.isFinite(endYear) ||
    !Number.isFinite(endMonth)
  ) {
    return false;
  }

  const target = toMonthIndex(entryValue.year, entryValue.month);
  return (
    target >= toMonthIndex(startYear, startMonth) &&
    target <= toMonthIndex(endYear, endMonth)
  );
};

export const mapYearToReportingPeriod = (reportingYears = [], entryValue) => {
  if (entryValue == null || entryValue === "") {
    return "";
  }

  if (!Array.isArray(reportingYears) || reportingYears.length === 0) {
    return String(entryValue);
  }

  const parsed = parseYearMonth(entryValue);

  if (!parsed) {
    return String(entryValue);
  }

  const reportingYear =
    parsed.month != null
      ? reportingYears.find((ry) => isMonthWithinReportingYear(ry, parsed))
      : reportingYears.find(
          (ry) => Number(ry?.startYear) === Number(parsed.year),
        );

  return reportingYear
    ? formatReportingYearPeriod(reportingYear)
    : parsed.month == null
      ? String(parsed.year)
      : `${parsed.year}-${String(parsed.month).padStart(2, "0")}`;
};

/**
 * Get category-specific styling
 * @param {string} category - Disclosure category
 * @returns {string} Tailwind classes for badge styling
 */
export const getCategoryTone = (category) => {
  switch (category) {
    case "Environmental":
      return "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-200";
    case "Social":
      return "bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-200";
    case "Governance":
      return "bg-purple-100 text-purple-700 dark:bg-purple-500/15 dark:text-purple-200";
    default:
      return "bg-muted text-muted-foreground";
  }
};
